import { useNavigate } from 'react-router-dom';

export function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050a0f] text-gray-300">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="text-[#00ffd0] hover:text-[#00ffd0]/80 mb-8 flex items-center gap-2 text-sm"
        >
          &larr; Back to Blurp.ie
        </button>

        <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: February 20, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              Blurp.ie ("we", "our", or "us") operates the Blurp.ie mobile application and website.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use our application. Please read this policy carefully.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Information We Collect</h2>
            <h3 className="text-white font-medium mb-2">Personal Information</h3>
            <ul className="list-disc list-inside space-y-1 mb-4">
              <li>Name and email address (when you create an account)</li>
              <li>Profile information you choose to provide</li>
            </ul>
            <h3 className="text-white font-medium mb-2">Location Data</h3>
            <ul className="list-disc list-inside space-y-1 mb-4">
              <li>GPS location during active running sessions (with your permission)</li>
              <li>Location is used to fetch real-time air quality and weather data for your area</li>
              <li>Route data from your recorded activities</li>
            </ul>
            <h3 className="text-white font-medium mb-2">Activity Data</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Running metrics (distance, pace, duration)</li>
              <li>Environmental data along your route (AQI, PM2.5, temperature, humidity)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Provide real-time air quality monitoring during your runs</li>
              <li>Track and display your running activities and environmental exposure</li>
              <li>Generate activity summaries and exportable snapshots</li>
              <li>Improve our services and user experience</li>
              <li>Send important updates about the service (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Data Storage and Security</h2>
            <p>
              Your data is stored securely using Supabase, a trusted cloud database platform.
              We implement industry-standard security measures including encrypted connections (HTTPS),
              row-level security policies, and secure authentication tokens to protect your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Third-Party Services</h2>
            <p className="mb-2">We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-white">Supabase</strong> — Authentication and data storage</li>
              <li><strong className="text-white">OpenWeatherMap / WAQI</strong> — Air quality and weather data</li>
              <li><strong className="text-white">Mapbox</strong> — Map display and navigation</li>
              <li><strong className="text-white">Stripe</strong> — Payment processing (for premium features)</li>
            </ul>
            <p className="mt-2">
              Each of these services has their own privacy policies governing how they handle data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Location Permissions</h2>
            <p>
              The app requests location permission to provide air quality data relevant to your current position
              during runs. Location tracking is only active during recorded activities. You can revoke location
              permissions at any time through your device settings, though this will limit the app's core functionality.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Data Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties.
              We may share anonymized, aggregated data for research purposes related to air quality
              and public health. Your activity data is private by default.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Access and download your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of non-essential communications</li>
              <li>Withdraw consent for location tracking at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Children's Privacy</h2>
            <p>
              Our service is not intended for users under the age of 13. We do not knowingly collect
              personal information from children under 13. If you believe we have collected information
              from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new policy on this page and updating the "Last updated" date. You are
              advised to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2 text-[#00ffd0]">support@blurp.ie</p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-gray-500 text-xs">
          &copy; {new Date().getFullYear()} Blurp.ie. All rights reserved.
        </div>
      </div>
    </div>
  );
}
